import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { AdaptedPlatformContent, SocialPlatform } from '@/lib/api';
import { PlatformBadge } from './PlatformBadge';
import { PLATFORM_CHARACTER_LIMITS } from './platform-constants';

interface PlatformContentEditorProps {
  platform: SocialPlatform;
  content: AdaptedPlatformContent;
  editedText: string;
  isApproved: boolean;
  onChange: (platform: SocialPlatform, text: string) => void;
  onApprove: (platform: SocialPlatform, approved: boolean) => void;
}

export function PlatformContentEditor({
  platform,
  content,
  editedText,
  isApproved,
  onChange,
  onApprove,
}: PlatformContentEditorProps) {
  const limit = PLATFORM_CHARACTER_LIMITS[platform];
  const isOverLimit = editedText.length > limit;
  const approveId = `approve-${platform}`;

  return (
    <div
      className={`border rounded-lg p-4 space-y-3 transition-colors ${
        isApproved ? 'border-primary/50 bg-primary/5' : ''
      }`}
    >
      <div className="flex items-center justify-between">
        <PlatformBadge platform={platform} size="md" />
        <div className="flex items-center gap-3">
          <span
            className={`text-xs font-mono ${
              isOverLimit ? 'text-destructive font-bold' : 'text-muted-foreground'
            }`}
          >
            {editedText.length}/{limit}
          </span>
          <div className="flex items-center gap-1.5">
            <Checkbox
              id={approveId}
              checked={isApproved}
              onCheckedChange={(checked) => onApprove(platform, checked === true)}
            />
            <Label htmlFor={approveId} className="text-xs cursor-pointer select-none">
              Approve
            </Label>
          </div>
        </div>
      </div>

      <Textarea
        value={editedText}
        onChange={(e) => onChange(platform, e.target.value)}
        rows={4}
        className="resize-none text-sm"
        aria-label={`${platform} post content`}
      />

      {isOverLimit && (
        <p className="text-xs text-destructive">
          Content exceeds the {limit}-character limit for {platform}.
        </p>
      )}

      {content.suggestions.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">AI Tips</p>
          <ul className="space-y-1">
            {content.suggestions.map((tip, i) => (
              <li key={i} className="text-xs text-muted-foreground flex gap-1">
                <span className="text-primary">•</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
